import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Shuffle,
    ChevronsLeft,
    Clock,
    AlertCircle,
    CheckCircle,
    Calendar,
    Users,
    History,
    Gift,
    Eye,
    Key,
    Ticket,
    DollarSign
} from 'lucide-react';
import marketingService from '../services/marketingService';
import TreeStructure from './TreeStructure';

const LightMixCluster = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Основные состояния
    const [loading, setLoading] = useState(true);
    const [currentDraw, setCurrentDraw] = useState(null);
    const [drawHistory, setDrawHistory] = useState([]);
    const [userPositions, setUserPositions] = useState([]); // Активные позиции пользователя
    const [pendingTickets, setPendingTickets] = useState(0); // Билеты в очереди
    const [isActivated, setIsActivated] = useState(false);
    const [activationDetails, setActivationDetails] = useState(null);

    // UI состояния
    const [activeTab, setActiveTab] = useState('current'); // current, history
    const [showTree, setShowTree] = useState(false);
    const [treeParams, setTreeParams] = useState(null);

    // Покупка
    const [isPurchasing, setIsPurchasing] = useState(false);

    // Таймер до розыгрыша
    const [drawTimeRemaining, setDrawTimeRemaining] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (currentDraw) {
            checkUserStatus();
        }
    }, [currentDraw]);

    // Таймер до розыгрыша
    useEffect(() => {
        if (!currentDraw?.draw_date) {
            setDrawTimeRemaining(null);
            return;
        }

        const targetTime = new Date(currentDraw.draw_date).getTime();

        const updateDrawTimer = () => {
            const now = new Date().getTime();
            const distance = targetTime - now;

            if (distance <= 0) {
                setDrawTimeRemaining(null);
                return true;
            }

            setDrawTimeRemaining(distance);
            return false;
        };

        const shouldStop = updateDrawTimer();
        if (shouldStop) return;

        const interval = setInterval(() => {
            const shouldStop = updateDrawTimer();
            if (shouldStop) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [currentDraw]);

    const formatDrawTime = (milliseconds) => {
        if (!milliseconds || milliseconds <= 0) return null;

        const totalSeconds = Math.floor(milliseconds / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (days > 0) {
            return { days, hours, minutes, seconds, display: `${days}д ${hours}ч ${minutes}м` };
        }
        return {
            days: 0,
            hours,
            minutes,
            seconds,
            display: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        };
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [drawResponse, historyResponse] = await Promise.all([
                marketingService.getLightMixCurrentDraw(),
                marketingService.getLightMixHistory()
            ]);

            if (drawResponse.success) {
                setCurrentDraw(drawResponse.data.draw);
            }

            if (historyResponse.success) {
                setDrawHistory(historyResponse.data.history || []);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkUserStatus = async () => {
        if (!currentDraw) return;

        try {
            const response = await marketingService.getLightMixUserStatus(currentDraw.program);

            if (response.success) {
                setUserPositions(response.data.positions || []);
                setPendingTickets(response.data.pending_count || 0);
                setIsActivated(response.data.is_activated || false);
                setActivationDetails(response.data.activation_details);
            }
        } catch (error) {
            console.error('Error checking user status:', error);
        }
    };

    const handlePurchase = async () => {
        if (!currentDraw || isPurchasing) return;

        setIsPurchasing(true);

        try {
            const response = await marketingService.purchaseLightMixPlace({
                draw_id: currentDraw.id,
                program: currentDraw.program
            });

            if (response.success) {
                // Увеличиваем счётчик билетов в очереди
                setPendingTickets(prev => prev + 1);
                // Обновляем данные с сервера
                checkUserStatus();
            } else {
                alert(response.message || 'Ошибка при покупке места');
            }
        } catch (error) {
            alert(error.message || 'Ошибка при покупке места');
        } finally {
            setIsPurchasing(false);
        }
    };

    const handleViewTree = async (program) => {
        try {
            const response = await marketingService.getLightMixUserStatus(program);

            if (response.success) {
                if (response.data.is_activated || response.data.positions?.length > 0) {
                    setShowTree(true);
                    setTreeParams({
                        tarif: 'mix',
                        level: 4,
                        numberLevel: 1,
                        program: program
                    });
                } else {
                    alert('Вы не участвуете в этом розыгрыше');
                }
            }
        } catch (error) {
            alert(error.message || 'Ошибка при проверке статуса');
        }
    };

    const handleBackFromTree = () => {
        setSearchParams({});
        setShowTree(false);
        setTreeParams(null);
    };

    const handleBack = () => {
        navigate('/dashboard/clusters');
    };

    // Рендер наград
    const renderRewards = (info) => {
        if (!info) return null;

        const rewards = [];

        if (info.awards && parseFloat(info.awards) > 0) {
            rewards.push(
                <div key="money" className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
                    <DollarSign size={18} className="text-green-600" />
                    <span className="text-green-700 font-medium">{info.awards} USDT</span>
                </div>
            );
        }

        if (info.keys && parseInt(info.keys) > 0) {
            rewards.push(
                <div key="keys" className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                    <Key size={18} className="text-blue-600" />
                    <span className="text-blue-700 font-medium">Light Keys (ур. {info.keys})</span>
                </div>
            );
        }

        if (info.mix && parseInt(info.mix) > 0) {
            rewards.push(
                <div key="mix" className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-lg">
                    <Ticket size={18} className="text-purple-600" />
                    <span className="text-purple-700 font-medium">{info.mix} билет на след. Mix</span>
                </div>
            );
        }

        return rewards.length > 0 ? rewards : null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка...</p>
                </div>
            </div>
        );
    }

    // Показываем дерево
    if (showTree && treeParams) {
        return (
            <TreeStructure
                tarif={treeParams.tarif}
                level={treeParams.level}
                numberLevel={treeParams.numberLevel}
                program={treeParams.program}
                onBack={handleBackFromTree}
                clusterType="mix"
            />
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Шапка */}
            <div className="border-b px-4 py-3">
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <button className="mr-3 text-gray-500 hover:text-purple-500 transition-colors" onClick={handleBack}>
                            <ChevronsLeft size={20} />
                        </button>
                        <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white mr-2">
                                <Shuffle size={16} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Light Mix</h3>
                        </div>
                    </div>

                    {/* Табы */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setActiveTab('current')}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                activeTab === 'current'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            Текущий
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                                activeTab === 'history'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <History size={16} />
                            История
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-4 md:p-6">
                {activeTab === 'current' ? (
                    <>
                        {/* Текущий розыгрыш */}
                        {currentDraw ? (
                            <div className="space-y-6">
                                {/* Карточка розыгрыша */}
                                <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-white rounded-2xl border-2 border-purple-200 overflow-hidden">
                                    {/* Заголовок */}
                                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h2 className="text-xl font-bold text-white">
                                                    {currentDraw.display_name || currentDraw.title}
                                                </h2>
                                                {currentDraw.description && (
                                                    <p className="text-purple-100 text-sm mt-1">{currentDraw.description}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <div className="text-3xl font-bold text-white">4 $</div>
                                                <div className="text-purple-100 text-sm">за место</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Таймер до розыгрыша */}
                                    {drawTimeRemaining && (
                                        <div className="px-6 py-4 bg-gradient-to-r from-purple-100/50 to-pink-100/50 border-b border-purple-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={20} className="text-purple-600" />
                                                    <span className="text-gray-700 font-medium">До розыгрыша:</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    {formatDrawTime(drawTimeRemaining)?.days > 0 ? (
                                                        <div className="flex gap-2">
                                                            <div className="bg-purple-500 text-white px-3 py-1 rounded-lg font-bold">
                                                                {formatDrawTime(drawTimeRemaining).days}д
                                                            </div>
                                                            <div className="bg-purple-500 text-white px-3 py-1 rounded-lg font-bold">
                                                                {formatDrawTime(drawTimeRemaining).hours}ч
                                                            </div>
                                                            <div className="bg-purple-500 text-white px-3 py-1 rounded-lg font-bold">
                                                                {formatDrawTime(drawTimeRemaining).minutes}м
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="bg-purple-500 text-white px-4 py-2 rounded-lg font-bold text-lg tabular-nums">
                                                            {formatDrawTime(drawTimeRemaining)?.display}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Статистика */}
                                    <div className="px-6 py-4 grid grid-cols-2 gap-4">
                                        <div className="bg-white rounded-xl p-4 border border-purple-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                                    <Users size={20} className="text-purple-600" />
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-gray-800">
                                                        {currentDraw.participants_count || 0}
                                                    </div>
                                                    <div className="text-sm text-gray-500">Участников</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 border border-purple-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                                    <Ticket size={20} className="text-green-600" />
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-gray-800">
                                                        {currentDraw.total_positions_count || 0}
                                                    </div>
                                                    <div className="text-sm text-gray-500">Всего билетов</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Награды */}
                                    {currentDraw.information && (
                                        <div className="px-6 py-4 border-t border-purple-100">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                <Gift size={18} className="text-purple-500" />
                                                Вознаграждения
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {renderRewards(currentDraw.information)}
                                            </div>
                                        </div>
                                    )}

                                    {/* Мои билеты */}
                                    {(userPositions.length > 0 || pendingTickets > 0) && (
                                        <div className="px-6 py-4 border-t border-purple-100 bg-gradient-to-r from-green-50/50 to-purple-50/50">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                <Ticket size={18} className="text-purple-500" />
                                                Мои билеты
                                            </h4>
                                            <div className="flex flex-wrap gap-4">
                                                {/* Активные места */}
                                                {userPositions.length > 0 && (
                                                    <div className="flex items-center gap-2 bg-green-100 px-4 py-2 rounded-lg">
                                                        <CheckCircle size={18} className="text-green-600" />
                                                        <span className="font-medium text-green-700">
                                                            Активных: {userPositions.length}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Ожидающие билеты */}
                                                {pendingTickets > 0 && (
                                                    <div className="flex items-center gap-2 bg-yellow-100 px-4 py-2 rounded-lg">
                                                        <Clock size={18} className="text-yellow-600" />
                                                        <span className="font-medium text-yellow-700">
                                                            В очереди: {pendingTickets}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Всего */}
                                                <div className="flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-lg">
                                                    <Ticket size={18} className="text-purple-600" />
                                                    <span className="font-medium text-purple-700">
                                                        Всего: {userPositions.length + pendingTickets}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Кнопка покупки */}
                                    {currentDraw.status === 'active' && (
                                        <div className="px-6 py-4 border-t border-purple-100">
                                            <button
                                                onClick={handlePurchase}
                                                disabled={isPurchasing}
                                                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                                                    isPurchasing
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                                                }`}
                                            >
                                                {isPurchasing ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                                        Обработка...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Ticket size={24} />
                                                        Купить место за 4 $
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Shuffle size={40} className="text-gray-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">Нет активных розыгрышей</h3>
                                <p className="text-gray-500">Следите за обновлениями, скоро будет новый розыгрыш!</p>
                            </div>
                        )}
                    </>
                ) : (
                    /* История розыгрышей */
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">История розыгрышей</h3>

                        {drawHistory.length > 0 ? (
                            drawHistory.map((draw) => (
                                <div
                                    key={draw.id}
                                    className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
                                                {draw.program?.replace('mix_', '#')}
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-800">
                                                    {draw.display_name || draw.title}
                                                </h4>
                                                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={14} />
                                                        {draw.formatted_draw_date || draw.completed_at}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Users size={14} />
                                                        {draw.participants_count || 0} участников
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            {draw.user_participated && (
                                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                                    Участвовали
                                                </span>
                                            )}
                                            {draw.user_participated && (
                                                <button
                                                    onClick={() => handleViewTree(draw.program)}
                                                    className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-all"
                                                    title="Посмотреть дерево"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                    <History size={32} className="text-gray-400" />
                                </div>
                                <p className="text-gray-500">История розыгрышей пуста</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LightMixCluster;
